'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import api from '@/lib/axios';

interface SiteAyarlari {
  site_adi?: string;
  footer_aciklama?: string;
  footer_telefon?: string;
  footer_email?: string;
  footer_adres?: string;
  footer_copyright?: string;
  sosyal_facebook?: string;
  sosyal_instagram?: string;
  sosyal_youtube?: string;
}

interface Kategori {
  id: number;
  ad: string;
  slug: string;
  parent_id: number | null;
}

export default function Footer() {
  const [ayarlar, setAyarlar] = useState<SiteAyarlari>({});
  const [anaKategoriler, setAnaKategoriler] = useState<Kategori[]>([]);

  useEffect(() => {
    api.get('/site-ayarlari').then((res) => setAyarlar(res.data));
    api.get('/kategoriler').then((res) => {
      const ana = res.data
        .filter((k: Kategori) => k.parent_id === null)
        .sort((a: Kategori, b: Kategori) => (a as any).sira - (b as any).sira);
      setAnaKategoriler(ana);
    });
  }, []);

  return (
    <footer className="bg-[var(--color-gray-900)] text-[var(--color-gray-400)] mt-16">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-8 mb-8">

          {/* Logo ve Açıklama */}
          <div className="md:col-span-1">
            <Link href="/" className="flex items-center gap-2 mb-3">
              <span className="text-2xl">🤸</span>
              <span className="text-white text-lg font-bold">{ayarlar.site_adi || 'Karacaspor'}</span>
            </Link>
            <p className="text-sm leading-relaxed mb-4">
              {ayarlar.footer_aciklama}
            </p>
            <div className="flex gap-2">
              {ayarlar.sosyal_facebook && (
                <a href={ayarlar.sosyal_facebook} target="_blank" rel="noopener noreferrer"
                  className="w-8 h-8 bg-[var(--color-gray-700)] rounded-full flex items-center justify-center hover:bg-[var(--color-primary)] transition text-xs font-bold text-white">f</a>
              )}
              {ayarlar.sosyal_instagram && (
                <a href={ayarlar.sosyal_instagram} target="_blank" rel="noopener noreferrer"
                  className="w-8 h-8 bg-[var(--color-gray-700)] rounded-full flex items-center justify-center hover:bg-[var(--color-primary)] transition text-xs font-bold text-white">ig</a>
              )}
              {ayarlar.sosyal_youtube && (
                <a href={ayarlar.sosyal_youtube} target="_blank" rel="noopener noreferrer"
                  className="w-8 h-8 bg-[var(--color-gray-700)] rounded-full flex items-center justify-center hover:bg-red-600 transition text-xs font-bold text-white">yt</a>
              )}
            </div>
          </div>

          {/* Kategoriler */}
          <div>
            <h4 className="text-white font-semibold mb-4">Kategoriler</h4>
            <ul className="space-y-2 text-sm">
              {anaKategoriler.map((kat) => (
                <li key={kat.id}>
                  <Link href={`/${kat.slug}`} className="hover:text-white transition">{kat.ad}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Alışveriş */}
          <div>
            <h4 className="text-white font-semibold mb-4">Alışveriş</h4>
            <ul className="space-y-2 text-sm">
              <li><Link href="/siparislerim" className="hover:text-white transition">Siparişlerim</Link></li>
              <li><Link href="/sepet" className="hover:text-white transition">Sepetim</Link></li>
              <li><Link href="/hesabim" className="hover:text-white transition">Hesabım</Link></li>
            </ul>
          </div>

          {/* Müşteri Hizmetleri */}
          <div>
            <h4 className="text-white font-semibold mb-4">Müşteri Hizmetleri</h4>
            <ul className="space-y-2 text-sm">
              <li><Link href="/sikca-sorulan-sorular" className="hover:text-white transition">Sık Sorulan Sorular</Link></li>
              <li><Link href="/iptal-iade-politikasi" className="hover:text-white transition">İade ve Değişim</Link></li>
              <li><Link href="/teslimat-bilgileri" className="hover:text-white transition">Kargo Bilgisi</Link></li>
              <li><Link href="/gizlilik-politikasi" className="hover:text-white transition">Gizlilik Politikası</Link></li>
              <li><Link href="/kullanim-kosullari" className="hover:text-white transition">Kullanım Koşulları</Link></li>
              <li><Link href="/cerez-politikasi" className="hover:text-white transition">Çerez Politikası</Link></li>
            </ul>
          </div>

          {/* Kurumsal */}
          <div>
            <h4 className="text-white font-semibold mb-4">Kurumsal</h4>
            <ul className="space-y-2 text-sm">
              <li><Link href="/hakkimizda" className="hover:text-white transition">Hakkımızda</Link></li>
              <li><Link href="/iletisim" className="hover:text-white transition">İletişim</Link></li>
            </ul>
          </div>

          {/* İletişim */}
          <div>
            <h4 className="text-white font-semibold mb-4">İletişim</h4>
            <ul className="space-y-2 text-sm">
              {ayarlar.footer_telefon && <li>📞 {ayarlar.footer_telefon}</li>}
              {ayarlar.footer_email && <li>📧 {ayarlar.footer_email}</li>}
              {ayarlar.footer_adres && <li>📍 {ayarlar.footer_adres}</li>}
            </ul>
          </div>
        </div>

        {/* Alt Bar */}
        <div className="border-t border-[var(--color-gray-700)] pt-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
          <p>{ayarlar.footer_copyright || `© ${new Date().getFullYear()} Karacaspor`}</p>
          <div className="flex gap-4">
            <Link href="/kullanim-kosullari" className="hover:text-white transition">Kullanım Koşulları</Link>
            <Link href="/cerez-politikasi" className="hover:text-white transition">Çerez Politikası</Link>
            <Link href="/gizlilik-politikasi" className="hover:text-white transition">Gizlilik</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
